import { motion } from "framer-motion";
import { ArrowUpRight, Zap, Sparkles, Check } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import { TALENTHEALTHCHECK_URL } from "@/lib/links";
import { track } from "@/lib/analytics";

export default function SubHero() {
  return (
    <section
      id="propuesta"
      data-testid="subhero-section"
      className="relative bg-[#051912] text-[#F7F9F8] py-20 lg:py-28 overflow-hidden grain"
    >
      {/* Continuity accent — subtle gold divider top */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-white/10" />
      <div aria-hidden className="absolute top-0 left-0 h-px w-40 brand-gradient" />

      {/* Ambient orb */}
      <div
        aria-hidden
        className="absolute -bottom-40 right-[-100px] w-[520px] h-[520px] rounded-full blur-[140px] opacity-25"
        style={{ background: "radial-gradient(circle,#C9A84C 0%, transparent 70%)" }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <Reveal className="text-center max-w-3xl mx-auto mb-14 lg:mb-20">
          <div className="flex items-center justify-center gap-3 text-[11px] uppercase tracking-[0.24em] text-[#C9A84C] mb-8">
            <span className="w-8 h-px bg-[#C9A84C]" />
            El paso previo
            <span className="w-8 h-px bg-[#C9A84C]" />
          </div>

          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.1] max-w-[22ch] mx-auto">
            Antes de contratar un software de RRHH, necesitas saber{" "}
            <span className="font-serif-italic font-normal text-[#2EC48A]">qué</span> quieres gestionar con él.
          </h2>

          <p className="mt-8 text-white/70 text-base lg:text-lg leading-relaxed font-light max-w-2xl mx-auto text-center lg:max-w-none">
            OptimizaRRHH no compite con los softwares de gestión de personas.
          </p>
          <p className="mt-4 text-white/70 text-base lg:text-lg leading-relaxed font-light max-w-2xl mx-auto text-center lg:max-w-none">
            Es el paso previo que hace que esas herramientas tengan sentido.
          </p>
          <p className="mt-4 text-white/70 text-base lg:text-lg leading-relaxed font-light max-w-2xl mx-auto text-center lg:max-w-none">
            Te ayuda a construir la base antes de digitalizarla.
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-2 gap-5 max-w-4xl lg:max-w-5xl mx-auto">
          <motion.a
            href={TALENTHEALTHCHECK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="subhero-cta-diagnostic"
            onClick={() => track("cta_click", { location: "subhero", product: "diagnostic" })}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="group relative flex flex-col rounded-[1.75rem] border border-[#2EC48A]/40 bg-[linear-gradient(160deg,rgba(46,196,138,0.14),rgba(6,35,29,0.55)_55%)] shadow-[0_40px_100px_-40px_rgba(46,196,138,0.35),0_24px_48px_-24px_rgba(6,35,29,0.9)] p-7 lg:p-8 text-center hover:bg-[linear-gradient(160deg,rgba(46,196,138,0.2),rgba(6,35,29,0.5)_55%)] hover:border-[#2EC48A]/70 hover:-translate-y-1 transition-all duration-500 overflow-hidden"
          >
            <span className="absolute top-4 left-4 w-3 h-px bg-[#2EC48A]/70" />
            <span className="absolute top-4 left-4 w-px h-3 bg-[#2EC48A]/70" />
            <span className="absolute top-4 right-4 w-3 h-px bg-[#2EC48A]/70" />
            <span className="absolute top-4 right-4 w-px h-3 bg-[#2EC48A]/70" />
            <span className="absolute bottom-4 left-4 w-3 h-px bg-[#2EC48A]/70" />
            <span className="absolute bottom-4 left-4 w-px h-3 bg-[#2EC48A]/70" />
            <span className="absolute bottom-4 right-4 w-3 h-px bg-[#2EC48A]/70" />
            <span className="absolute bottom-4 right-4 w-px h-3 bg-[#2EC48A]/70" />
            <span aria-hidden className="absolute -top-16 -right-16 w-56 h-56 rounded-full blur-[80px] opacity-25 pointer-events-none" style={{ background: "radial-gradient(circle,#2EC48A 0%, transparent 70%)" }} />

            <div className="flex items-center justify-center gap-3 mb-7">
              <span className="inline-flex items-center gap-2.5 text-[10px] uppercase tracking-[0.24em] text-[#2EC48A]">
                <Zap size={13} strokeWidth={1.8} />
                Diagnóstico rápido
              </span>
              <span className="w-10 h-10 rounded-full brand-gradient grid place-items-center flex-shrink-0 group-hover:scale-105 transition-transform">
                <ArrowUpRight size={15} strokeWidth={1.8} className="text-white" />
              </span>
            </div>

            <img
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_rrhh-platform-1/artifacts/bd559216ddcf3060_mockup1.webp"
              alt="Informe del diagnóstico rápido — portada y páginas de resultados."
              loading="lazy"
              data-testid="subhero-diagnostic-report-image"
              className="mx-auto mb-7 w-24 lg:w-28 rounded-xl ring-1 ring-[#2EC48A]/60 shadow-[0_20px_40px_-16px_rgba(46,196,138,0.35)]"
            />

            <div className="flex items-baseline justify-center gap-2.5 mb-2">
              <span className="font-display font-semibold text-4xl lg:text-[2.9rem] leading-none text-white">€47</span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-white/45">≈ $54 USD</span>
            </div>
            <p className="text-white/55 text-[13px] leading-relaxed font-light mb-6">
              En 15 minutos sabes qué áreas de tu empresa necesitan atención.
            </p>

            <div className="h-px bg-white/10 mb-6" />

            <ul className="space-y-3 text-[13px] text-white/80 font-light flex-1 w-fit mx-auto text-left">
              {[
                "30 preguntas en 6 áreas clave",
                "Puntuación global y por área",
                "Informe PDF descargable",
                "Resultados inmediatos",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <span className="mt-0.5 w-[18px] h-[18px] rounded-full border border-[#2EC48A]/40 grid place-items-center flex-shrink-0">
                    <Check size={10} strokeWidth={2.4} className="text-[#2EC48A]" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>

            <div className="mt-7 inline-flex items-center gap-2 self-center text-[11px] uppercase tracking-[0.22em] text-[#2EC48A]">
              Empezar diagnóstico
              <ArrowUpRight size={12} strokeWidth={2} />
            </div>
          </motion.a>

          <motion.a
            href={TALENTHEALTHCHECK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="subhero-cta-consulting"
            onClick={() => track("cta_click", { location: "subhero", product: "consulting" })}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="group relative flex flex-col rounded-[1.75rem] border border-[#C9A84C]/60 bg-[linear-gradient(160deg,rgba(201,168,76,0.16),rgba(6,35,29,0.55)_55%)] shadow-[0_40px_100px_-36px_rgba(201,168,76,0.4),0_24px_48px_-24px_rgba(6,35,29,0.9)] p-7 lg:p-8 text-center hover:bg-[linear-gradient(160deg,rgba(201,168,76,0.22),rgba(6,35,29,0.5)_55%)] hover:border-[#C9A84C]/90 hover:-translate-y-1 transition-all duration-500 overflow-hidden"
          >
            <span className="absolute top-4 left-4 w-3 h-px bg-[#C9A84C]" />
            <span className="absolute top-4 left-4 w-px h-3 bg-[#C9A84C]" />
            <span className="absolute top-4 right-4 w-3 h-px bg-[#C9A84C]" />
            <span className="absolute top-4 right-4 w-px h-3 bg-[#C9A84C]" />
            <span className="absolute bottom-4 left-4 w-3 h-px bg-[#C9A84C]" />
            <span className="absolute bottom-4 left-4 w-px h-3 bg-[#C9A84C]" />
            <span className="absolute bottom-4 right-4 w-3 h-px bg-[#C9A84C]" />
            <span className="absolute bottom-4 right-4 w-px h-3 bg-[#C9A84C]" />
            <span aria-hidden className="absolute -top-16 -right-16 w-56 h-56 rounded-full blur-[80px] opacity-25 pointer-events-none" style={{ background: "radial-gradient(circle,#C9A84C 0%, transparent 70%)" }} />
            <span data-testid="subhero-badge-recommended" className="absolute top-0 left-1/2 -translate-x-1/2 inline-flex items-center bg-[#C9A84C] text-[#06231D] text-[9px] uppercase tracking-[0.2em] font-bold px-4 py-1.5 rounded-b-xl">
              Más completo
            </span>

            <div className="flex items-center justify-center gap-3 mb-7">
              <span className="inline-flex items-center gap-2.5 text-[10px] uppercase tracking-[0.24em] text-[#C9A84C]">
                <Sparkles size={13} strokeWidth={1.8} />
                Consultoría completa
              </span>
              <span className="w-10 h-10 rounded-full bg-[#C9A84C] grid place-items-center flex-shrink-0 group-hover:scale-105 transition-transform">
                <ArrowUpRight size={15} strokeWidth={1.8} className="text-[#06231D]" />
              </span>
            </div>

            <img
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_rrhh-platform-1/artifacts/0d77d713955926d8_Copy%20of%20Untitled%20Design.webp"
              alt="Informe ejecutivo de la consultoría premium — portada, checklist y contraste de percepciones."
              loading="lazy"
              data-testid="subhero-consulting-report-image"
              className="mx-auto mb-7 w-24 lg:w-28 rounded-xl ring-1 ring-[#C9A84C]/60 shadow-[0_20px_40px_-16px_rgba(201,168,76,0.4)]"
            />

            <div className="flex items-baseline justify-center gap-2.5 mb-2">
              <span className="font-display font-semibold text-4xl lg:text-[2.9rem] leading-none text-white">€397</span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-white/45">$460 USD</span>
            </div>
            <p className="text-white/55 text-[13px] leading-relaxed font-light mb-6">
              Análisis profesional en profundidad: metodología propia de consultoría, potenciada con inteligencia artificial.
            </p>

            <div className="h-px bg-white/10 mb-6" />

            <ul className="space-y-3 text-[13px] text-white/85 font-light flex-1 w-fit mx-auto text-left">
              {[
                "Diagnóstico rápido incluido gratis",
                "35 preguntas adicionales específicas (37 en equipos internacionales)",
                "Informe personalizado en 24 horas, no en semanas de consultoría tradicional",
                "Plan de acción a 30 / 90 / 180 días",
                "Checklist interactiva con seguimiento",
                "Contraste anónimo con la percepción de tu equipo",
                "7 herramientas inteligentes construidas a partir de tu diagnóstico",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <span className="mt-0.5 w-[18px] h-[18px] rounded-full border border-[#C9A84C]/50 grid place-items-center flex-shrink-0">
                    <Check size={10} strokeWidth={2.4} className="text-[#C9A84C]" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>

            <div className="mt-7 inline-flex items-center gap-2 self-center text-[11px] uppercase tracking-[0.22em] text-[#C9A84C]">
              Empezar consultoría
              <ArrowUpRight size={12} strokeWidth={2} />
            </div>
          </motion.a>

          <div className="sm:col-span-2 mt-2 text-center text-[11px] uppercase tracking-[0.2em] text-white/40">
            Compra y acceso en talenthealthcheck.com
          </div>
        </div>
      </div>
    </section>
  );
}
